import React, { useEffect, useMemo, useState } from 'react';
import { useDeals } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { fmtMoney } from '../lib/format';
import { dealTalentIds } from '../lib/deals';
import { ChevronLeft, ChevronRight, CalendarDays, Grid3x3, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import InitialsAvatar from '../components/InitialsAvatar';
import DealDialog from '../components/deals/DealDialog';
import { useNavigate } from 'react-router-dom';

// Same deterministic palette as InitialsAvatar for talent colours
const TALENT_PALETTE = [
  { bg: '#fbe8ef', fg: '#8f4a63' },
  { bg: '#e6dbf1', fg: '#6b4e8a' },
  { bg: '#f5e3d5', fg: '#8b5a2c' },
  { bg: '#dceff0', fg: '#3a7075' },
  { bg: '#e8ecc7', fg: '#6b7530' },
  { bg: '#f3dede', fg: '#8a3a3a' },
  { bg: '#dce3f0', fg: '#3d5891' },
  { bg: '#efe1e9', fg: '#7a4a5f' },
];
const hashCode = (s) => {
  const str = String(s || '');
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const colourFor = (name) => TALENT_PALETTE[hashCode(name || 'x') % TALENT_PALETTE.length];

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const buildGrid = (year, month) => {
  // Return a 6×7 array of Date objects starting from Monday of week containing the 1st
  const first = new Date(year, month, 1);
  const dow = (first.getDay() + 6) % 7; // 0 = Mon
  const start = new Date(year, month, 1 - dow);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
};

const STATUS_DOT = { concept: '#94a3b8', drafted: '#f59e0b', approved: '#10b981', scheduled: '#a78bfa' };

const Calendar = () => {
  const { user } = useAuth();
  const { deals, talent, talentMap, brandMap } = useDeals();
  const navigate = useNavigate();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [talentFilter, setTalentFilter] = useState('all');
  const isManager = ['manager', 'talent_manager'].includes(user?.role);

  // View mode: 'deals' (existing) | 'client' (social-posts per client)
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('calendar.viewMode') || 'deals'; } catch { return 'deals'; }
  });
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(() => {
    try { return localStorage.getItem('social.clientId') || ''; } catch { return ''; }
  });
  const [clientPosts, setClientPosts] = useState([]);
  const [dayPopover, setDayPopover] = useState(null); // { dateStr, posts } | null

  useEffect(() => {
    try { localStorage.setItem('calendar.viewMode', viewMode); } catch {}
  }, [viewMode]);

  // Load clients (manager only)
  useEffect(() => {
    if (!isManager) return;
    (async () => {
      try {
        const { data } = await api.get('/clients');
        setClients(data || []);
        if (!clientId && data?.length) setClientId(data[0].id);
      } catch {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager]);

  // Load posts for the selected client (all plans)
  useEffect(() => {
    if (viewMode !== 'client' || !clientId) { setClientPosts([]); return; }
    try { localStorage.setItem('social.clientId', clientId); } catch {}
    (async () => {
      try {
        const { data } = await api.get('/social/posts', { params: { client_id: clientId } });
        setClientPosts((data || []).filter((p) => Boolean(p.posting_date)));
      } catch { setClientPosts([]); }
    })();
  }, [viewMode, clientId]);

  // Build a map: date-string → array of { deal, kind: 'due' | 'live', talents }
  const eventsByDay = useMemo(() => {
    const m = {};
    const add = (dateStr, evt) => {
      if (!m[dateStr]) m[dateStr] = [];
      m[dateStr].push(evt);
    };
    for (const d of deals) {
      const tIds = dealTalentIds(d);
      if (talentFilter !== 'all' && !tIds.includes(talentFilter)) continue;
      if (d.due_date) add(d.due_date, { deal: d, kind: 'due', talentIds: tIds });
      if (d.go_live_date) add(d.go_live_date, { deal: d, kind: 'live', talentIds: tIds });
    }
    return m;
  }, [deals, talentFilter]);

  // Social posts grouped by posting_date (ISO YYYY-MM-DD)
  const postsByDay = useMemo(() => {
    const m = {};
    for (const p of clientPosts) {
      const key = String(p.posting_date).slice(0, 10);
      if (!m[key]) m[key] = [];
      m[key].push(p);
    }
    return m;
  }, [clientPosts]);

  const openSocialForPost = (p) => {
    try {
      localStorage.setItem('social.clientId', p.client_id);
      if (p.plan_id) localStorage.setItem(`social.planId.${p.client_id}`, p.plan_id);
    } catch {}
    navigate('/social');
  };

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const grid = useMemo(() => buildGrid(year, month), [year, month]);
  const todayStr = ymd(today);
  const selectedDeal = selectedDealId ? deals.find((d) => d.id === selectedDealId) : null;

  const prev = () => setCursor(new Date(year, month - 1, 1));
  const next = () => setCursor(new Date(year, month + 1, 1));
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));

  // Legend: only talents who actually have events this month
  const talentsInMonth = useMemo(() => {
    const s = new Set();
    grid.forEach((d) => {
      if (d.getMonth() !== month) return;
      (eventsByDay[ymd(d)] || []).forEach((e) => e.talentIds.forEach((id) => s.add(id)));
    });
    return talent.filter((t) => s.has(t.user_id));
  }, [grid, eventsByDay, month, talent]);

  return (
    <div className="p-6 lg:p-8" data-testid="calendar-page">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <CalendarDays className="w-6 h-6 text-pink-500" />
        <h1 className="text-2xl font-semibold text-slate-900">calendar</h1>
        <span className="text-sm text-slate-500">{viewMode === 'client' ? 'social posts scheduled for the day' : 'deals due, content going live'}</span>

        {/* View mode toggle */}
        {isManager && (
          <div className="inline-flex items-center rounded-md border border-slate-200 bg-white overflow-hidden ml-2" data-testid="cal-view-toggle">
            <button
              onClick={() => setViewMode('deals')}
              className={`inline-flex items-center gap-1.5 px-3 h-9 text-sm ${viewMode === 'deals' ? 'bg-mint-50 text-mint-700' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid="cal-view-deals"
            >
              <Handshake className="w-4 h-4" /> deals
            </button>
            <button
              onClick={() => setViewMode('client')}
              className={`inline-flex items-center gap-1.5 px-3 h-9 text-sm border-l border-slate-200 ${viewMode === 'client' ? 'bg-mint-50 text-mint-700' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid="cal-view-client"
            >
              <Grid3x3 className="w-4 h-4" /> client
            </button>
          </div>
        )}

        {/* Client selector (only in client mode) */}
        {isManager && viewMode === 'client' && (
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="min-w-[200px] h-9" data-testid="cal-client-select"><SelectValue placeholder="pick a client" /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={prev} className="w-9 h-9 p-0" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="text-sm font-medium min-w-[140px] text-center text-slate-800">{MONTHS[month]} {year}</div>
          <Button variant="ghost" size="sm" onClick={next} className="w-9 h-9 p-0" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={goToday} className="text-xs">today</Button>
        </div>
      </div>

      {/* Talent filter + legend (deals mode only) */}
      {viewMode === 'deals' && isManager && talent.length > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <button
            onClick={() => setTalentFilter('all')}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition ${talentFilter === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-200 hover:border-slate-300 text-slate-600'}`}
            data-testid="cal-filter-all"
          >
            all talents
          </button>
          {talentsInMonth.map((t) => {
            const c = colourFor(t.name);
            const active = talentFilter === t.user_id;
            return (
              <button
                key={t.user_id}
                onClick={() => setTalentFilter(active ? 'all' : t.user_id)}
                style={active ? { background: c.fg, color: '#fff', borderColor: c.fg } : { background: c.bg, color: c.fg, borderColor: c.bg }}
                className="text-[11px] px-2.5 py-1 rounded-full border flex items-center gap-1.5 font-medium transition"
                data-testid={`cal-filter-${t.user_id}`}
              >
                <InitialsAvatar name={t.name} size={16} className="text-[9px] ring-1 ring-white" />
                {t.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Month grid */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50">
          {WEEKDAYS.map((d) => <div key={d} className="text-[11px] uppercase tracking-wide text-slate-500 font-medium px-3 py-2 text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((d, idx) => {
            const inMonth = d.getMonth() === month;
            const dateStr = ymd(d);
            const isToday = dateStr === todayStr;
            const events = eventsByDay[dateStr] || [];
            return (
              <div
                key={idx}
                className={`min-h-[110px] border-t border-r border-slate-100 p-1.5 ${inMonth ? 'bg-white' : 'bg-slate-50/40'} ${idx % 7 === 6 ? 'border-r-0' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-xs font-medium ${
                      isToday ? 'bg-pink-500 text-white rounded-full w-5 h-5 inline-flex items-center justify-center' :
                      inMonth ? 'text-slate-700' : 'text-slate-300'
                    }`}
                  >
                    {d.getDate()}
                  </span>
                </div>
                <div className="mt-1 space-y-0.5">
                  {viewMode === 'client' ? (
                    (() => {
                      const dayPosts = postsByDay[dateStr] || [];
                      const preview = dayPosts.slice(0, 3);
                      return (
                        <>
                          <div className="flex flex-wrap gap-1">
                            {preview.map((p) => (
                              <button
                                key={p.id}
                                onClick={() => openSocialForPost(p)}
                                className="relative w-10 h-10 rounded-md overflow-hidden ring-1 ring-slate-200 hover:ring-pink-400 transition"
                                title={`${p.pillar ? p.pillar + ' · ' : ''}${p.status}${p.copy ? ' — ' + p.copy.slice(0, 60) : ''}`}
                                data-testid={`cal-social-tile-${p.id}`}
                              >
                                {p.cover_url ? (
                                  <img src={p.cover_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                ) : (
                                  <div className="w-full h-full bg-slate-100 flex items-center justify-center text-[9px] text-slate-400">no cover</div>
                                )}
                                <span className="absolute top-0.5 left-0.5 w-1.5 h-1.5 rounded-full ring-1 ring-white" style={{ background: STATUS_DOT[p.status] || STATUS_DOT.concept }} />
                              </button>
                            ))}
                          </div>
                          {dayPosts.length > 3 && (
                            <button
                              onClick={() => setDayPopover({ dateStr, posts: dayPosts })}
                              className="text-[10px] text-slate-500 hover:text-pink-600 px-1"
                              data-testid={`cal-social-more-${dateStr}`}
                            >
                              +{dayPosts.length - 3} more
                            </button>
                          )}
                        </>
                      );
                    })()
                  ) : (
                    <>
                      {events.slice(0, 4).map((e, i) => {
                        const primary = talentMap[e.talentIds[0]];
                        const c = colourFor(primary?.name || 'unassigned');
                        const brand = brandMap[e.deal.brand_id];
                        return (
                          <button
                            key={`${e.deal.id}-${e.kind}-${i}`}
                            onClick={() => setSelectedDealId(e.deal.id)}
                            style={{ background: c.bg, color: c.fg }}
                            className="w-full text-left text-[10px] rounded px-1.5 py-0.5 hover:brightness-95 transition truncate leading-tight"
                            title={`${e.kind === 'due' ? 'DUE' : 'LIVE'} — ${e.deal.title}${primary ? ` (${primary.name})` : ''}`}
                            data-testid={`cal-event-${e.deal.id}-${e.kind}`}
                          >
                            <span className="font-semibold mr-1">{e.kind === 'due' ? '●' : '◆'}</span>
                            {brand?.name ? `${brand.name} ` : ''}{primary?.name ? `× ${primary.name}` : e.deal.title}
                          </button>
                        );
                      })}
                      {events.length > 4 && (
                        <div className="text-[10px] text-slate-500 px-1.5">+{events.length - 4} more</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Key legend */}
      {viewMode === 'deals' ? (
        <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="text-slate-400">●</span> due date</span>
          <span className="inline-flex items-center gap-1"><span className="text-slate-400">◆</span> go‑live date</span>
          <span className="ml-auto text-slate-400">click an event to open the deal</span>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: STATUS_DOT.concept }} /> concept</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: STATUS_DOT.drafted }} /> drafted</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: STATUS_DOT.approved }} /> approved</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: STATUS_DOT.scheduled }} /> scheduled</span>
          <span className="ml-auto text-slate-400">click a tile to open the plan</span>
        </div>
      )}

      {selectedDeal && (
        <DealDialog
          open={Boolean(selectedDeal)}
          onOpenChange={(v) => !v && setSelectedDealId(null)}
          deal={selectedDeal}
        />
      )}

      {/* Day popover — shows all posts scheduled for a day */}
      {dayPopover && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4"
          onClick={() => setDayPopover(null)}
          data-testid="cal-day-popover"
        >
          <div
            className="bg-white rounded-xl shadow-xl border border-slate-200 max-w-md w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-800">{dayPopover.dateStr}</div>
              <div className="text-[11px] text-slate-400">{dayPopover.posts.length} posts</div>
            </div>
            <div className="p-3 grid grid-cols-3 gap-2">
              {dayPopover.posts.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { openSocialForPost(p); setDayPopover(null); }}
                  className="relative aspect-square rounded-md overflow-hidden ring-1 ring-slate-200 hover:ring-pink-400 transition"
                  data-testid={`cal-day-post-${p.id}`}
                >
                  {p.cover_url ? (
                    <img src={p.cover_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-slate-100 flex items-center justify-center text-[10px] text-slate-400">no cover</div>
                  )}
                  <span className="absolute top-1 left-1 w-2 h-2 rounded-full ring-1 ring-white" style={{ background: STATUS_DOT[p.status] || STATUS_DOT.concept }} />
                  {p.pillar && <span className="absolute bottom-0 inset-x-0 px-1.5 py-0.5 text-[9px] text-white bg-gradient-to-t from-black/60 to-transparent truncate">{p.pillar}</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
